import { useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ColorPickerCustom } from './ColorPickerCustom';

/** A solid color, hex `#rrggbb` (or `#rrggbbaa` if alpha is needed
 *  later — the current pickers normalize to 6-char hex). */
export type SolidColor = { type: 'solid'; color: string };

/** Single stop in a gradient: position 0..1 along the axis, color hex. */
export type GradientStop = { offset: number; color: string };

/** Linear gradient: angle in degrees (0 = bottom-to-top, 90 = left-to-right
 *  per CSS convention) and an ordered list of color stops. */
export type LinearGradientValue = {
  type: 'linear';
  angle: number;
  stops: GradientStop[];
};

/** Radial gradient: center is implicit (50%, 50%) and radius is implicit
 *  (the larger half-axis). Tier 1+ may surface custom center / radius. */
export type RadialGradientValue = {
  type: 'radial';
  stops: GradientStop[];
};

export type ColorValue = SolidColor | LinearGradientValue | RadialGradientValue;

/** Render a `ColorValue` to a CSS `background` string. Used for the
 *  popover's preview swatches and the trigger button background. */
export function colorValueToCss(value: ColorValue): string {
  if (value.type === 'solid') return value.color;
  const stops = value.stops
    .slice()
    .sort((a, b) => a.offset - b.offset)
    .map((s) => `${s.color} ${Math.round(s.offset * 100)}%`)
    .join(', ');
  if (value.type === 'linear') return `linear-gradient(${value.angle}deg, ${stops})`;
  return `radial-gradient(circle, ${stops})`;
}

/** Promote a legacy hex string to a `ColorValue`. Accepts the
 *  Fabric-friendly `'transparent'` keyword and forwards as-is. */
export function hexToColorValue(hex: string): ColorValue {
  if (hex === 'transparent') return { type: 'solid', color: 'transparent' };
  return { type: 'solid', color: hex };
}

/** When a consumer only supports solid colors (e.g. brush color, fill
 *  bucket), reduce a `ColorValue` to its first stop / solid color so
 *  it can still be used. Lossy by design. */
export function colorValueToHex(value: ColorValue): string {
  if (value.type === 'solid') return value.color;
  return value.stops[0]?.color ?? '#000000';
}

interface ColorSwatchPopoverProps {
  value: ColorValue;
  onChange: (value: ColorValue) => void;
  /** Allow Linear / Radial tabs. Default `true`. Set `false` for
   *  contexts that can only consume solid colors (brush, fill tool). */
  allowGradient?: boolean;
  /** Aria + tooltip label for the trigger swatch. */
  label?: string;
  /** Optional extra classes for the trigger swatch. */
  className?: string;
}

const DEFAULT_LINEAR: LinearGradientValue = {
  type: 'linear',
  angle: 90,
  stops: [
    { offset: 0, color: '#000000' },
    { offset: 1, color: '#ffffff' },
  ],
};

const DEFAULT_RADIAL: RadialGradientValue = {
  type: 'radial',
  stops: [
    { offset: 0, color: '#ffffff' },
    { offset: 1, color: '#000000' },
  ],
};

/** Color swatch trigger + popover. Both modes open a popover with
 *  the Tier 3 ColorPickerCustom; gradient mode wraps it in
 *  Solid / Linear / Radial tabs so the popover can edit gradients
 *  too. Recent-colors strip + EyeDropper + HSB/RGB/HSL/Lab/Hex tabs
 *  all live inside `ColorPickerCustom` — see that file for the
 *  picker UX details. */
export function ColorSwatchPopover({
  value,
  onChange,
  allowGradient = true,
  label,
  className,
}: ColorSwatchPopoverProps) {
  // Track the last solid / linear / radial value separately so the
  // user can switch between tabs without losing their gradient setup
  // when they toggle to Solid and back. In solid-only mode the
  // linear / radial drafts are never read but cost nothing to track.
  const [solidDraft, setSolidDraft] = useState<SolidColor>(
    value.type === 'solid' ? value : { type: 'solid', color: '#000000' },
  );
  const [linearDraft, setLinearDraft] = useState<LinearGradientValue>(
    value.type === 'linear' ? value : DEFAULT_LINEAR,
  );
  const [radialDraft, setRadialDraft] = useState<RadialGradientValue>(
    value.type === 'radial' ? value : DEFAULT_RADIAL,
  );

  // Keep drafts in sync when `value` changes externally (e.g. user
  // picked a different layer with different settings).
  useEffect(() => {
    if (value.type === 'solid') setSolidDraft(value);
    else if (value.type === 'linear') setLinearDraft(value);
    else if (value.type === 'radial') setRadialDraft(value);
  }, [value]);

  const activeTab = value.type;

  const setActiveTab = (next: 'solid' | 'linear' | 'radial') => {
    if (next === 'solid') onChange(solidDraft);
    else if (next === 'linear') onChange(linearDraft);
    else onChange(radialDraft);
  };

  const updateSolidHex = (hex: string) => {
    const next: SolidColor = { type: 'solid', color: hex };
    setSolidDraft(next);
    onChange(next);
  };

  const updateLinear = (next: LinearGradientValue) => {
    setLinearDraft(next);
    onChange(next);
  };

  const updateRadial = (next: RadialGradientValue) => {
    setRadialDraft(next);
    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label ?? 'Pick color'}
          title={label ?? 'Pick color'}
          className={`w-8 h-8 rounded border border-border cursor-pointer overflow-hidden ${className ?? ''}`}
          style={{
            background:
              value.type === 'solid' && value.color === 'transparent'
                ? 'repeating-conic-gradient(#ddd 0% 25%, transparent 0% 50%) 50% / 12px 12px'
                : colorValueToCss(value),
          }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="start">
        {allowGradient && (
          <div className="flex gap-1">
            {(['solid', 'linear', 'radial'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`flex-1 px-2 py-1 text-xs rounded-md border transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        {value.type === 'solid' && (
          <ColorPickerCustom
            value={solidDraft.color === 'transparent' ? '#000000' : solidDraft.color}
            onChange={updateSolidHex}
          />
        )}

        {value.type === 'linear' && <LinearPanel value={linearDraft} onChange={updateLinear} />}

        {value.type === 'radial' && <RadialPanel value={radialDraft} onChange={updateRadial} />}
      </PopoverContent>
    </Popover>
  );
}

/** Linear gradient panel: angle wheel + stops list + preview bar. */
function LinearPanel({
  value,
  onChange,
}: {
  value: LinearGradientValue;
  onChange: (v: LinearGradientValue) => void;
}) {
  const sortedStops = useMemo(
    () => [...value.stops].sort((a, b) => a.offset - b.offset),
    [value.stops],
  );

  const updateStop = (index: number, patch: Partial<GradientStop>) => {
    onChange({
      ...value,
      stops: sortedStops.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });
  };

  const addStop = () => {
    if (value.stops.length >= 8) return;
    // Drop a new stop midway between the last two.
    const last = sortedStops[sortedStops.length - 1];
    const prev = sortedStops[sortedStops.length - 2];
    const offset = prev ? (prev.offset + last.offset) / 2 : 0.5;
    onChange({
      ...value,
      stops: [...sortedStops, { offset, color: prev?.color ?? '#888888' }],
    });
  };

  const removeStop = (index: number) => {
    if (value.stops.length <= 2) return;
    onChange({
      ...value,
      stops: sortedStops.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-2">
      <GradientPreviewBar
        stops={sortedStops}
        background={`linear-gradient(${value.angle}deg, ${sortedStops
          .map((s) => `${s.color} ${Math.round(s.offset * 100)}%`)
          .join(', ')})`}
      />
      <label className="flex items-center gap-2">
        <span className="text-xs w-12 text-muted-foreground">Angle</span>
        <input
          type="range"
          min={0}
          max={360}
          value={value.angle}
          onChange={(e) => onChange({ ...value, angle: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-10 text-right tabular-nums">{value.angle}°</span>
      </label>
      <StopsList
        stops={sortedStops}
        onUpdateStop={updateStop}
        onRemoveStop={removeStop}
        onAddStop={addStop}
        canAdd={value.stops.length < 8}
        canRemove={value.stops.length > 2}
      />
    </div>
  );
}

/** Radial gradient panel: stops list + preview circle. Center +
 *  radius are implicit (centered, fitting the largest axis); Tier
 *  1+ can surface custom center / radius if needed. */
function RadialPanel({
  value,
  onChange,
}: {
  value: RadialGradientValue;
  onChange: (v: RadialGradientValue) => void;
}) {
  const sortedStops = useMemo(
    () => [...value.stops].sort((a, b) => a.offset - b.offset),
    [value.stops],
  );

  const updateStop = (index: number, patch: Partial<GradientStop>) => {
    onChange({
      ...value,
      stops: sortedStops.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });
  };

  const addStop = () => {
    if (value.stops.length >= 8) return;
    const last = sortedStops[sortedStops.length - 1];
    const prev = sortedStops[sortedStops.length - 2];
    const offset = prev ? (prev.offset + last.offset) / 2 : 0.5;
    onChange({
      ...value,
      stops: [...sortedStops, { offset, color: prev?.color ?? '#888888' }],
    });
  };

  const removeStop = (index: number) => {
    if (value.stops.length <= 2) return;
    onChange({
      ...value,
      stops: sortedStops.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-2">
      <GradientPreviewBar
        stops={sortedStops}
        background={`radial-gradient(circle, ${sortedStops
          .map((s) => `${s.color} ${Math.round(s.offset * 100)}%`)
          .join(', ')})`}
      />
      <StopsList
        stops={sortedStops}
        onUpdateStop={updateStop}
        onRemoveStop={removeStop}
        onAddStop={addStop}
        canAdd={value.stops.length < 8}
        canRemove={value.stops.length > 2}
      />
    </div>
  );
}

/** Visual preview bar showing the rendered gradient. */
function GradientPreviewBar({ stops, background }: { stops: GradientStop[]; background: string }) {
  return (
    <div
      className="w-full h-8 rounded border border-border"
      style={{ background }}
      aria-hidden="true"
    >
      {/* Stop tick marks (decorative) */}
      <div className="relative w-full h-full">
        {stops.map((s, i) => (
          <span
            key={i}
            className="absolute top-0 h-full w-0.5 bg-white/70 mix-blend-difference"
            style={{ left: `${Math.round(s.offset * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/** Reusable stops list editor: per-stop offset slider + color
 *  picker + delete; "+ Add stop" footer button. */
function StopsList({
  stops,
  onUpdateStop,
  onRemoveStop,
  onAddStop,
  canAdd,
  canRemove,
}: {
  stops: GradientStop[];
  onUpdateStop: (index: number, patch: Partial<GradientStop>) => void;
  onRemoveStop: (index: number) => void;
  onAddStop: () => void;
  canAdd: boolean;
  canRemove: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase">Stops</span>
        <button
          type="button"
          onClick={onAddStop}
          disabled={!canAdd}
          className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
          aria-label="Add stop"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {stops.map((stop, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            type="color"
            value={stop.color}
            onChange={(e) => onUpdateStop(i, { color: e.target.value })}
            className="w-7 h-7 rounded border border-border cursor-pointer shrink-0"
            aria-label={`Stop ${i + 1} color`}
          />
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(stop.offset * 100)}
            onChange={(e) => onUpdateStop(i, { offset: Number(e.target.value) / 100 })}
            className="flex-1"
            aria-label={`Stop ${i + 1} position`}
          />
          <span className="text-[11px] w-10 text-right tabular-nums">
            {Math.round(stop.offset * 100)}%
          </span>
          <button
            type="button"
            onClick={() => onRemoveStop(i)}
            disabled={!canRemove}
            className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:pointer-events-none"
            aria-label={`Delete stop ${i + 1}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
